import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import type { PackAsset } from '../lib/assets';
import type { PackDetail } from '../lib/packs';
import { formatBytes } from '../lib/uploadFile';

/**
 * Publish / unpublish action bar for the pack editor.
 *
 * Renders one button at a time depending on `pack.pack_state`:
 *
 *   - `draft`       → "Phát hành" (publish) → confirmation summarises
 *                     asset count, total bytes, and target version
 *                     (Requirement 23.1).
 *   - `published`   → "Gỡ phát hành" (unpublish) → confirmation warns
 *                     about catalog refresh impact (Requirement 23.4).
 *   - `unpublished` → "Phát hành lại" using the same publish flow.
 *
 * The actual API calls happen in the parent (`PackEditorPage`) — this
 * component is purely presentational so the editor can centralise its
 * mutation + refetch + toast wiring.
 *
 * Validates Requirements 23.1, 23.2, 23.3, 23.4.
 */

export interface PublishBarProps {
  pack: PackDetail;
  assets: PackAsset[];
  isPublishing: boolean;
  isUnpublishing: boolean;
  onPublish: () => Promise<void>;
  onUnpublish: () => Promise<void>;
}

export default function PublishBar({
  pack,
  assets,
  isPublishing,
  isUnpublishing,
  onPublish,
  onUnpublish,
}: PublishBarProps): JSX.Element {
  const [confirmingPublish, setConfirmingPublish] = useState(false);
  const [confirmingUnpublish, setConfirmingUnpublish] = useState(false);

  const totalBytes = assets.reduce((sum, a) => sum + a.byteSize, 0);

  const isPublished = pack.pack_state === 'published';

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-sm">
        <p className="font-medium text-slate-900">
          Trạng thái:{' '}
          <span className="font-normal">
            {pack.pack_state === 'draft'
              ? 'Bản nháp'
              : pack.pack_state === 'published'
                ? `Đã phát hành (v${pack.active_version})`
                : 'Đã gỡ phát hành'}
          </span>
        </p>
        <p className="text-xs text-slate-500">
          {assets.length} asset · {formatBytes(totalBytes)} ·{' '}
          {isPublished
            ? 'Người dùng đang truy cập được'
            : `Phiên bản tiếp theo: v${pack.nextVersion}`}
        </p>
      </div>

      <div className="flex gap-2">
        {!isPublished ? (
          <button
            type="button"
            onClick={() => setConfirmingPublish(true)}
            disabled={isPublishing || assets.length === 0}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-emerald-300"
            title={
              assets.length === 0
                ? 'Pack cần có ít nhất một asset trước khi phát hành'
                : undefined
            }
          >
            {isPublishing ? 'Đang phát hành…' : 'Phát hành'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmingUnpublish(true)}
            disabled={isUnpublishing}
            className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 shadow-sm hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isUnpublishing ? 'Đang gỡ…' : 'Gỡ phát hành'}
          </button>
        )}
      </div>

      <Dialog.Root
        open={confirmingPublish}
        onOpenChange={(open) => {
          if (!open) setConfirmingPublish(false);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-900/40" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-slate-200 bg-white p-6 shadow-lg focus:outline-none">
            <Dialog.Title className="text-lg font-semibold text-slate-900">
              Phát hành phiên bản v{pack.nextVersion}?
            </Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-slate-500">
              Phiên bản này sẽ trở nên truy cập được công khai và không thể
              chỉnh sửa. Hãy kiểm tra số liệu bên dưới.
            </Dialog.Description>
            <dl className="mt-4 space-y-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-slate-500">Số asset</dt>
                <dd className="font-medium text-slate-900">{assets.length}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Tổng dung lượng</dt>
                <dd className="font-medium text-slate-900">
                  {formatBytes(totalBytes)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Phiên bản đích</dt>
                <dd className="font-medium text-slate-900">
                  v{pack.nextVersion}
                </dd>
              </div>
            </dl>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmingPublish(false)}
                disabled={isPublishing}
                className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Huỷ
              </button>
              <button
                type="button"
                onClick={async () => {
                  await onPublish();
                  setConfirmingPublish(false);
                }}
                disabled={isPublishing}
                className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-emerald-300"
              >
                {isPublishing ? 'Đang phát hành…' : 'Phát hành'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root
        open={confirmingUnpublish}
        onOpenChange={(open) => {
          if (!open) setConfirmingUnpublish(false);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-900/40" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-slate-200 bg-white p-6 shadow-lg focus:outline-none">
            <Dialog.Title className="text-lg font-semibold text-slate-900">
              Gỡ phát hành pack?
            </Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-slate-500">
              Người dùng sẽ mất quyền truy cập sau khi catalog được làm
              mới. Phiên bản hiện tại (v{pack.active_version}) vẫn được giữ
              lại để có thể phát hành lại sau.
            </Dialog.Description>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmingUnpublish(false)}
                disabled={isUnpublishing}
                className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Huỷ
              </button>
              <button
                type="button"
                onClick={async () => {
                  await onUnpublish();
                  setConfirmingUnpublish(false);
                }}
                disabled={isUnpublishing}
                className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-amber-300"
              >
                {isUnpublishing ? 'Đang gỡ…' : 'Gỡ phát hành'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
